import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useActivitiesContext } from "./ActivitiesContext";
import { useActivities } from "./useActivities";
import { ActivityEventSource } from "./sources/ActivityEventSource";
import { AsyncTaskService } from "./sources/AsyncTaskService";
import { ActivityEventKind } from "./sources/providers/types";
import { acknowledgeEvents } from "../../../../backendapi/activityApi";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import type { ActivityViewData } from "./types";

type UseWorkspaceActivitiesOptions = {
    workspaceId?: string;
    userId?: string;
    /**
     * Which activity event kinds to include. Undefined = all kinds.
     * Each consumer declares what it shows (e.g. [AsyncTask] for the
     * core app navbar, [CIModelChange] for Rapid).
     */
    kinds?: ActivityEventKind[];
};

/**
 * All event kinds except AsyncTask. AsyncTaskService handles async tasks
 * from the `async_tasks` collection, so ActivityEventSource must not
 * duplicate them from `activity_events`.
 */
const ALL_EVENT_SOURCE_KINDS = Object.values(ActivityEventKind).filter(
    (k) => k !== ActivityEventKind.AsyncTask
);

/**
 * Subscribes to `activity_event_interactions` for the given workspace+user
 * and returns the set of acknowledged (read) activity IDs.
 *
 * A single listener shared across all sources — neither ActivityEventSource
 * nor AsyncTaskService need to track read state themselves.
 */
function useAcknowledgedIds(
    workspaceId: string,
    userId: string,
    database: DatabaseAdapter | null
): Set<string> {
    const [ackIds, setAckIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!workspaceId || !userId || !database) {
            setAckIds(new Set());
            return;
        }

        return database.onQuerySnapshot(
            "activity_event_interactions",
            {
                where: [
                    ["owner", "==", workspaceId],
                    ["user", "==", userId]
                ]
            },
            (docs) => {
                const ids = new Set<string>();
                for (const doc of docs) {
                    const data = doc.data();
                    if (data?.ackAt && data?.activityEvent) {
                        ids.add(data.activityEvent as string);
                    }
                }
                setAckIds(ids);
            },
            // eslint-disable-next-line no-console
            (err) => console.error("activity_event_interactions listener error:", err)
        );
    }, [workspaceId, userId, database]);

    return ackIds;
}

/**
 * Subscribes to activity sources for a workspace and returns activities
 * with read-state management (persistent via Firestore, optimistic via
 * local state) and mark-as-read callbacks.
 *
 * By default reads workspaceId/userId from ActivitiesContext.
 * Pass overrides when the component lives inside a different provider
 * tree (e.g. Navbar inside Rapid's provider but needs core app workspace).
 */
export function useWorkspaceActivities(options?: UseWorkspaceActivitiesOptions) {
    const ctx = useActivitiesContext();

    const workspaceId = options?.workspaceId ?? ctx.workspaceId;
    const userId = options?.userId ?? ctx.userId;
    const { database } = ctx;
    const kinds = options?.kinds;

    // Derive kinds for ActivityEventSource: caller kinds with AsyncTask
    // excluded (AsyncTaskService handles those from the async_tasks collection).
    // Stabilized via serialized key to avoid referential instability.
    const kindsKey = kinds?.join(",") ?? "";
    const eventSourceKinds = useMemo(
        () => (kinds ?? ALL_EVENT_SOURCE_KINDS).filter((k) => k !== ActivityEventKind.AsyncTask),
        [kindsKey]
    );

    // AsyncTaskService queries `async_tasks` directly — only include it when
    // the caller asks for all kinds (undefined) or explicitly includes AsyncTask.
    const includeAsyncTasks = !kinds || kinds.includes(ActivityEventKind.AsyncTask);

    const sources = useMemo(
        () =>
            workspaceId && database
                ? [
                      ...(includeAsyncTasks ? [new AsyncTaskService(workspaceId, database)] : []),
                      ...(eventSourceKinds.length > 0
                          ? [
                                new ActivityEventSource(
                                    workspaceId,
                                    userId,
                                    database,
                                    eventSourceKinds
                                )
                            ]
                          : [])
                  ]
                : [],
        [workspaceId, userId, database, eventSourceKinds, includeAsyncTasks]
    );

    const { activities: sourceActivities, activeCount, isLoading } = useActivities(sources);

    // --- Read state (persistent + optimistic) ---

    const ackIds = useAcknowledgedIds(workspaceId, userId, database);
    const [localReadIds, setLocalReadIds] = useState<Set<string>>(new Set());

    const activities = useMemo(
        () =>
            sourceActivities.map((a) =>
                ackIds.has(a.id) || localReadIds.has(a.id) ? { ...a, read: true } : a
            ),
        [sourceActivities, ackIds, localReadIds]
    );

    const activitiesRef = useRef(activities);
    activitiesRef.current = activities;

    /** Exposed for ActivityCenter to apply read state to invite activities
     *  constructed outside the hook. Remove when invites become a proper
     *  ActivitySource — the hook will handle their read state internally. */
    const isRead = useCallback(
        (id: string) => ackIds.has(id) || localReadIds.has(id),
        [ackIds, localReadIds]
    );

    // Invite/join activities use synthetic IDs (e.g. "invite-{wid}", "join-{id}")
    // that have no matching activity_events document. The backend writes an
    // activity_event_interactions doc regardless — this creates orphan interaction
    // records. They're harmless (read state still works via useAcknowledgedIds)
    // and will stop being created once invites become a proper ActivitySource.
    // TODO[cfviotti]: clean up orphan interaction docs after InviteSource refactor.
    const onMarkRead = useCallback(
        (activity: ActivityViewData) => {
            setLocalReadIds((prev) => {
                const next = new Set(prev);
                next.add(activity.id);
                return next;
            });
            acknowledgeEvents(workspaceId, [activity.id]).catch((err) =>
                // eslint-disable-next-line no-console
                console.error("Failed to acknowledge activity event:", err)
            );
        },
        [workspaceId]
    );

    const onMarkAllRead = useCallback(() => {
        const unreadIds = activitiesRef.current.filter((a) => !a.read).map((a) => a.id);
        if (unreadIds.length === 0) return;
        setLocalReadIds((prev) => {
            const next = new Set(prev);
            for (const id of unreadIds) next.add(id);
            return next;
        });
        acknowledgeEvents(workspaceId, unreadIds).catch((err) =>
            // eslint-disable-next-line no-console
            console.error("Failed to acknowledge activity events:", err)
        );
    }, [workspaceId]);

    return { activities, isRead, onMarkRead, onMarkAllRead, activeCount, isLoading };
}
